import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCustomerHistory } from '../../../Apis/Api';
import { toast } from 'react-hot-toast';
import {
  FiUser,
  FiShoppingBag,
  FiClock,
  FiDollarSign,
  FiSearch,
  FiArrowLeft
} from 'react-icons/fi';

const CustomerHistoryPage = () => {
  const [phone, setPhone] = useState('');
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    return () => {
      if (searchTimeout) clearTimeout(searchTimeout);
    };
  }, [searchTimeout]);

  const fetchCustomerHistory = async (phoneNumber) => {
    try {
      setLoading(true);
      const { data } = await getCustomerHistory(phoneNumber);
      setHistory(data);
    } catch (error) {
      toast.error('Customer not found or no purchases');
      setHistory(null);
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneChange = (e) => {
    const input = e.target.value.replace(/\D/g, '').slice(0, 10);
    setPhone(input);
    
    if (searchTimeout) clearTimeout(searchTimeout);
    
    if (input.length === 10) {
      setSearchTimeout(
        setTimeout(() => fetchCustomerHistory(input), 500)
      );
    }
  };

  return (
    <div className="p-4 max-w-4xl mx-auto">

      <h1 className="text-2xl font-bold mb-6 flex items-center gap-2">
        <FiUser /> Customer Purchase History
      </h1>

      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="relative">
          <FiSearch className="absolute left-3 top-3 text-gray-400" />
          <input
            type="tel"
            value={phone}
            onChange={handlePhoneChange}
            placeholder="Enter customer phone number"
            className="w-full pl-10 pr-4 py-2 rounded border focus:outline-none focus:ring-2 focus:ring-blue-500"
            maxLength="10"
          />
        </div>
      </div>

      {loading && <div className="text-center p-4">Searching...</div>}

      {history && !loading && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b">
            <h2 className="text-xl font-bold flex items-center gap-2">
              {history.checkouts[0]?.customer?.name || 'Guest Customer'}
              <span className="text-sm font-normal text-gray-500">
                ({phone})
              </span>
            </h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
              <div className="p-3 bg-blue-50 rounded">
                <div className="text-sm text-gray-600">Total Transactions</div>
                <div className="text-xl font-bold text-blue-600">
                  {history.summary.totalTransactions}
                </div>
              </div>
              <div className="p-3 bg-green-50 rounded">
                <div className="text-sm text-gray-600">Total Spent</div>
                <div className="text-xl font-bold text-green-600">
                  NPR {history.summary.totalAmount.toFixed(2)}
                </div>
              </div>
              <div className="p-3 bg-purple-50 rounded">
                <div className="text-sm text-gray-600">First Purchase</div>
                <div className="text-sm font-medium text-purple-600">
                  {new Date(history.summary.firstPurchase).toLocaleDateString()}
                </div>
              </div>
              <div className="p-3 bg-orange-50 rounded">
                <div className="text-sm text-gray-600">Total Items</div>
                <div className="text-xl font-bold text-orange-600">
                  {history.summary.totalItems}
                </div>
              </div>
            </div>
          </div>

          <div className="divide-y">
            {history.checkouts.map((checkout, index) => (
              <div key={checkout._id} className="p-4 hover:bg-gray-50">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <div className="font-medium flex items-center gap-2">
                      <FiClock />
                      {new Date(checkout.createdAt).toLocaleDateString()}
                      <span className="text-xs px-2 py-1 rounded-full bg-gray-100 capitalize">
                        {checkout.paymentMethod}
                      </span>
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      {checkout.remarks || 'No remarks'}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-blue-600">
                      NPR {checkout.totalAmount.toFixed(2)}
                    </div>
                    <div className="text-sm text-gray-500">
                      {checkout.cart.length} items
                    </div>
                  </div>
                </div>

                <div className="mt-3 grid gap-2">
                  {checkout.cart.map((item, i) => (
                    <div key={i} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                      <div>
                        <div className="font-medium">
                          {item.quantity}x {item.name}
                        </div>
                        <div className="text-xs text-gray-500">
                          {item.size}/{item.color}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-medium">
                          NPR {(item.price * item.quantity).toFixed(2)}
                        </div>
                        <div className="text-xs text-gray-500">
                          NPR {item.price} each
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerHistoryPage;